import { CookieKeys } from "@outschool/data-schemas";
import {
  AnalyticsPluginRestProxyTrackArgs,
  RestProxyBrowserClient,
  SEARCH_IMPRESSIONS_INTAKE_TOPIC,
  SEARCH_IMPRESSIONS_INTAKE_TOPIC_SCHEMA_ID,
} from "@outschool/kafka-rest-proxy-client";
import { useIsBot } from "@outschool/ui-utils";
import Cookies from "js-cookie";

import pkg from "../../lib/pkg";

let kafkaRestProxyClient: RestProxyBrowserClient;

export default function usePromotedEventLogger(properties: any = null) {
  const isBot = useIsBot();
  if (!kafkaRestProxyClient) {
    // In SSR context, we don't want to do any logging, just return stubs
    if (typeof window === "undefined") {
      return {
        logImpression: () => null,
        logAction: () => null,
        handleError: () => null,
      };
    }
    if (pkg.env !== "test" && !pkg.kafkaRestProxy) {
      throw new Error(
        "Cannot call usePromotedEventLogger without environment variables defined"
      );
    }
    kafkaRestProxyClient = new RestProxyBrowserClient({
      endpoint: pkg.kafkaRestProxy,
    });
  }

  const loggedInUserExperimentUid = Cookies.get(
    CookieKeys.LoggedInUserExperimentUid
  );
  const loggedOutUserExperimentUid = Cookies.get(
    CookieKeys.LoggedOutUserExperimentUid
  );

  const eventLogger = isBot
    ? {
        logImpression: () => null,
        logAction: () => null,
        handleError: () => null,
      }
    : createPromotedEventLogger({
        loggedInUserExperimentUid,
        loggedOutUserExperimentUid,
        properties,
      });
  return eventLogger;
}

function getEventTrackArgs(payload: any): AnalyticsPluginRestProxyTrackArgs {
  return {
    event: payload,
    topic: SEARCH_IMPRESSIONS_INTAKE_TOPIC,
    topic_schema_id: SEARCH_IMPRESSIONS_INTAKE_TOPIC_SCHEMA_ID,
  };
}

export const createPromotedEventLogger = ({
  loggedInUserExperimentUid,
  loggedOutUserExperimentUid,
  properties,
}: {
  loggedInUserExperimentUid?: string;
  loggedOutUserExperimentUid?: string;
  properties: any;
}) => {
  const logImpression = (impression: any) => {
    impression.source_type = impression.insertionId
      ? "DELIVERY"
      : "CLIENT_BACKEND";

    /* Stop gap solution. Something introduced this new field
     * which is now causing issues on Promoted side
     */
    if (typeof impression === "object" && "sourceType" in impression) {
      delete impression["sourceType"];
    }

    impression.properties = { struct: properties };

    try {
      kafkaRestProxyClient.sendEvent(
        getEventTrackArgs({
          original_timestamp: new Date().toISOString(),
          event_name: "impression",
          event_data: JSON.stringify(impression),
          user_uid: loggedInUserExperimentUid,
          anonymous_id: loggedOutUserExperimentUid,
          topic: SEARCH_IMPRESSIONS_INTAKE_TOPIC,
          user_agent: navigator.userAgent.substring(0, 200),
        })
      );
    } catch {
      console.error("Was unable to log");
    }
  };

  const logAction = (action: any) => {
    action.properties = { struct: { ...action.properties, ...properties } };

    kafkaRestProxyClient.sendEvent(
      getEventTrackArgs({
        original_timestamp: new Date().toISOString(),
        event_name: "impression",
        event_data: JSON.stringify(action),
        user_uid: loggedInUserExperimentUid,
        anonymous_id: loggedOutUserExperimentUid,
        topic: SEARCH_IMPRESSIONS_INTAKE_TOPIC,
        user_agent: navigator.userAgent.substring(0, 200),
      })
    );
  };

  const handleError = (e: any) => {
    console.error(e);
  };

  return {
    logImpression,
    logAction,
    handleError,
  };
};
